import * as React from 'react'

import { Icon } from '~/components/icon/Icon'
import { styled } from '~/styled'
import { useCallbackRefState } from '~/utilities/hooks/useCallbackRef'
import { OptionalTooltipWrapper } from '~/utilities/optional-tooltip-wrapper/OptionalTooltipWrapper'

import {
  badgeSemanticNames,
  colorSchemes as badgeColorSchemes
} from './Badge.colorscheme.config'
import { BadgeContext, BadgeProvider } from './Badge.context'
import { BadgeIcon } from './BadgeIcon'
import { BadgeText } from './BadgeText'

const StyledBadge = styled(
  'div',
  {
    base: [
      'flex',
      'justify-center',
      'items-center',
      'rounded-sm',
      'min-w-0',
      'border',
      'border-white',
      'font-body',
      '*:not-last:mr-1'
    ],
    variants: {
      emphasis: {
        white: ['text-(--text-on-white)', 'bg-white'],
        subtle: ['text-(--text-subtle)', 'bg-(--background-subtle)'],
        bold: ['text-(--text-bold)', 'bg-(--background-bold)']
      },
      size: {
        xs: ['px-1', 'py-0'],
        sm: ['px-1', 'py-0.5'],
        md: ['px-2', 'py-1']
      },
      isClickable: {
        true: ['cursor-pointer']
      },
      highlighted: {
        true: []
      }
    },
    compoundVariants: [
      {
        isClickable: true,
        emphasis: 'white',
        class: [
          'hover:text-(--text-on-white)',
          'hover:bg-[color-mix(in_srgb,(--text-on-white),transparent_92%)]'
        ]
      },
      {
        isClickable: true,
        emphasis: 'subtle',
        class: [
          'hover:bg-(--background-subtle-hover)',
          'hover:text-(--text-subtle-hover)'
        ]
      },
      {
        isClickable: true,
        emphasis: 'bold',
        class: [
          'hover:bg-(--background-bold-hover)',
          'hover:text-(--text-bold)'
        ]
      },
      {
        emphasis: 'white',
        highlighted: true,
        class: [
          'text-(--text-on-white)',
          'bg-[color-mix(in_srgb,(--text-on-white),transparent_92%)]'
        ]
      },
      {
        emphasis: 'subtle',
        highlighted: true,
        class: ['text-(--text-subtle-hover)', '(--bg-background-subtle-hover)']
      },
      {
        emphasis: 'bold',
        highlighted: true,
        class: ['text-(--text-bold)', 'bg-(--background-bold-hover)']
      }
    ]
  },
  { enabledResponsiveVariants: true }
)

export type TBadgeProps = React.ComponentProps<typeof StyledBadge> & {
  theme?: keyof typeof badgeColorSchemes
  overflow?: React.ComponentProps<typeof BadgeText>['overflow']
}

type TBadgeInnerProps = Omit<TBadgeProps, 'size' | 'overflow'>

const BadgeInner = React.forwardRef<HTMLDivElement, TBadgeInnerProps>(
  ({ theme = 'info', emphasis = 'subtle', children, style, ...rest }, ref) => {
    const { size, overflow, isOverflowing } = React.useContext(BadgeContext)
    const [badgeElRef, setBadgeElRef] = useCallbackRefState()
    React.useImperativeHandle(ref, () => badgeElRef as HTMLDivElement)

    const label = badgeElRef?.textContent

    const isInfoOnly = badgeSemanticNames.includes(theme)

    const isClickable =
      Object.keys(rest).includes('onClick') ||
      Object.keys(rest).includes('href')

    return (
      <OptionalTooltipWrapper
        hasTooltip={overflow === 'ellipsis' && isOverflowing}
        label={label}
      >
        <StyledBadge
          role="status"
          emphasis={emphasis}
          size={size}
          style={{
            ...badgeColorSchemes[theme],
            ...style
          }}
          {...rest}
          ref={setBadgeElRef}
          isClickable={isClickable && !isInfoOnly}
        >
          {React.Children.map(children, (child) => {
            if (typeof child === 'string' || typeof child === 'number') {
              return <BadgeText>{child}</BadgeText>
            }
            if (
              React.isValidElement<React.ComponentPropsWithoutRef<typeof Icon>>(
                child
              ) &&
              child.type === Icon
            ) {
              return <BadgeIcon {...child.props} />
            }
            return child
          })}
        </StyledBadge>
      </OptionalTooltipWrapper>
    )
  }
)

const BadgeComponent = React.forwardRef<HTMLDivElement, TBadgeProps>(
  ({ size = 'sm', overflow = 'wrap', ...rest }, ref) => {
    return (
      <BadgeProvider size={size} overflow={overflow}>
        <BadgeInner {...rest} ref={ref} />
      </BadgeProvider>
    )
  }
)

export const Badge = Object.assign(BadgeComponent, {
  Icon: BadgeIcon,
  Text: BadgeText
})

BadgeComponent.displayName = 'Badge'
